/**
 * Reviewing one sponsor's logos, from view-sp-review in docs/prototype.html.
 *
 * ORGANISED AROUND SLOTS, NOT FILES. The strip shows every version the event
 * asked for, including the ones nobody has sent — a missing version is the
 * thing that needs chasing, so it has to be as visible as a submitted one.
 * Anything sent outside the requested list appears after them as an Extra.
 *
 * DECISIONS ARE STAGED. Approving or rejecting writes a decision and tells
 * nobody. The sponsor hears once, when the consolidated review is sent, with
 * every version and note together. Eight variations should not mean eight
 * emails, and a reviewer changing their mind halfway should not have already
 * told someone.
 *
 * PREVIEWS AND DERIVATIVES ARE REAL NOW. This file used to say there were
 * none, because rasterising untrusted vector files in-process was too large an
 * attack surface for a thumbnail. That is still true, which is why the
 * rendering happens once at ingest, in its own process, and this screen only
 * ever reads the stored results. See server/src/lib/renditions.ts.
 *
 * EVERY DERIVATIVE IS OPTIONAL AND THIS SCREEN NEVER ASSUMES ONE. EPS is never
 * rendered by decision, a render can fail on a malformed file, and assets
 * uploaded before the feature have none until the backfill reaches them. All
 * three fall back to the format badge that was here before.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { formatLabel, isVector } from '../lib/fileFormat';
import { T } from '../lib/copyEdit.tsx';

interface Variation {
  id: string;
  orientation: string;
  treatment: string;
}

interface Submission {
  id: string;
  eventVariationId: string | null;
  status: 'pending' | 'approved' | 'fix_requested';
  decisionNote: string | null;
  sponsorOrientation: string | null;
  sponsorTreatment: string | null;
  correctedByUserId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  /** Heuristic notes recorded at upload. Never a reason the file was refused —
   *  it was not — only something worth a second look. */
  signals: { code: string; reviewerMessage: string }[] | null;
  /** Where this file got to in the malware scan. Nothing reaches storage
   *  un-scanned, so in practice this is 'clean'; it is shown rather than
   *  assumed because a checklist that cannot fail proves nothing. */
  scanStatus: string;
  /** Who supplied this file. Staff uploads are recorded as such because a
   *  sponsor may later dispute what they sent. */
  uploadSource: 'sponsor' | 'staff';
  uploadedByName: string | null;
  /** Set when this came from the library rather than being sent for this
   *  event. ISO string, or null. */
  carriedOverAt: string | null;
  /** Set when the sponsor said they were happy to reuse it. */
  sponsorConfirmedAt: string | null;
  /** Which generated copies exist for this file. Names only: the server never
   *  sends storage keys, and this list is what decides whether an export
   *  button is offered or a format badge shown instead. */
  renditions: RenditionName[];
}

type RenditionName = 'thumb' | 'preview' | 'png' | 'jpeg';

interface Slot {
  variation: Variation | null;
  label: string;
  sublabel: string;
  submission: Submission | null;
}

/** Whether anything is actually chasing the versions nobody has sent, and why
 *  not. Worked out server-side by the same helper the reminders panel uses. */
interface Chasing {
  scheduled: boolean;
  reason: string | null;
}

interface Progress {
  requested: number;
  submitted: number;
  approved: number;
  decided: number;
  missing: number;
  readyToSend: boolean;
}

/**
 * The formats the ORIGINAL-bytes preview endpoint can serve directly.
 *
 * Kept as a fallback rather than removed. An asset uploaded before renditions
 * existed has none until the backfill reaches it, and for these three the
 * original is already something a browser renders, so it can stand in
 * meanwhile. PDF, AI and EPS were never in this set and still are not: for
 * them, a rendition or a badge are the only two options.
 */
const DIRECTLY_PREVIEWABLE = ['image/png', 'image/jpeg', 'image/svg+xml'];

/** The two the export buttons offer, in the order the prototype lists them. */
const EXPORTS: { name: RenditionName; label: string }[] = [
  { name: 'png', label: 'PNG · transparent' },
  { name: 'jpeg', label: 'JPEG · white' },
];

/**
 * Which id identifies a slot.
 *
 * NOT THE ARRAY INDEX, and that is a fix rather than a preference. The slot
 * list is requested versions followed by Extras, and its tail shifts whenever
 * anything is relabelled, deleted or uploaded. Holding a selection by index
 * meant the panel could silently come to be pointing at a different
 * submission than the one the reviewer chose.
 *
 * A requested slot always has a variation; an Extra always has a submission.
 * So one of the two is always present.
 */
function slotKey(slot: Slot): string {
  return slot.variation?.id ?? slot.submission?.id ?? '';
}

/** The image source for a slot's generated copy, or null when there is none
 *  and the caller should fall back. */
function renditionSrc(
  submission: Submission,
  name: RenditionName,
  base: string,
): string | null {
  return submission.renditions.includes(name)
    ? `${base}/${submission.id}/rendition/${name}`
    : null;
}

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The file standing for a slot, in one line under the heading.
 *
 * This replaced a three row File / Format / Size table. Those are one fact
 * about one file, and three labelled rows to say it pushed the decision
 * buttons below the fold on a laptop.
 */
function fileLine(slot: Slot): string {
  const s = slot.submission;
  if (!s) return 'Nothing sent for this version yet';

  const parts = [formatLabel(s.mimeType, s.filename), fileSize(s.sizeBytes)];
  /* An Extra's heading IS its filename, so repeating it here would be the
     same string twice, one line apart. */
  return slot.variation ? [s.filename, ...parts].join(' · ') : parts.join(' · ');
}

/** The download glyph, on the two controls that hand over a file. */
function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

/**
 * What sits below the strip for a version nobody has sent.
 *
 * SELECTING ONE USED TO OPEN NOTHING. Every other state loads a panel under
 * the strip, so the one state that actually needs chasing was the only one
 * where clicking appeared to do nothing at all.
 *
 * It says the two things a reviewer needs at that moment: that the sponsor
 * still owes it, and that it does not block sending the review. The second is
 * the one people get wrong — the send gate counts decisions on what HAS been
 * sent, and a missing version rides along in the same email as outstanding.
 */
/**
 * What to say about a version nobody has sent.
 *
 * IT USED TO PROMISE A CHASE UNCONDITIONALLY — "reminders are still chasing
 * it" — which holds only for a sponsor who has been invited, is not withdrawn,
 * has reminders on and is still inside the deadline. Every other case was a
 * reassurance that the app was handling something it had stopped handling, and
 * the most common one is the most misleading: a sponsor nobody has invited yet
 * is not being chased by anything.
 *
 * The reason comes from the same helper that drives the reminders panel, so
 * the two cannot disagree about the same sponsor.
 */
function chaseLine(chasing: Chasing | null): string {
  if (!chasing) return 'It goes into the review email as outstanding rather than holding up the send.';

  if (chasing.scheduled) {
    return 'Reminders are still chasing it, and it goes into the review email as outstanding rather than holding up the send.';
  }

  switch (chasing.reason) {
    case 'withdrawn':
      return 'This sponsor is withdrawn, so nobody is chasing it.';
    case 'never invited':
      return 'Reminders start once they are invited. Nothing is chasing this yet.';
    case 'just invited':
      return 'They were invited in the last day, so the first reminder has not been sent yet.';
    case 'chaser paused':
      return 'Reminders are switched off for this sponsor, so nothing is chasing it.';
    case 'held after a manual message':
      return 'Reminders are on hold after a message was sent by hand, so nothing automatic is chasing it right now.';
    case 'deadline passed':
    case 'schedule finished':
      return 'Reminders have finished, so this one needs a person.';
    case 'no deadline set':
      return 'The event has no deadline, so no reminders are scheduled for it.';
    case 'everything approved':
    case 'waiting on our review':
      return 'Nothing automatic is chasing this one at the moment.';
    default:
      return 'Nothing is chasing this one at the moment.';
  }
}

function Waiting({ chasing }: { chasing: Chasing | null }) {
  return (
    <div className="rv-open">
      <div className="rv-stage rv-stage--waiting">
        <div>
          <span className="rv-waiting-title"><T k="review.waiting.title" d="Still waiting on this one" /></span>
          <p className="fdesc">
            The event asked for it and the sponsor has not sent it. {chaseLine(chasing)}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Review({
  clientId,
  eventId,
  eventSponsorId,
}: {
  clientId: string;
  eventId: string;
  eventSponsorId: string;
}) {
  const base = `/api/clients/${clientId}/events/${eventId}/sponsors/${eventSponsorId}/review`;

  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [chasing, setChasing] = useState<Chasing | null>(null);
  const [sentAt, setSentAt] = useState<string | null>(null);
  /* Comes from the server, which is the only thing that knows. Defaults to
   * true so a failed load never invents a "nothing will be delivered"
   * reassurance that would be false on every real deployment. */
  const [emailConfigured, setEmailConfigured] = useState(true);
  /**
   * Which slot the decision panel is about, held BY IDENTITY.
   *
   * This was an array index, and three separate defects came out of that.
   * `slots` is requested versions followed by Extras, and its tail shifts on
   * every relabel, delete and upload, so an index could quietly come to mean a
   * different submission than the one that was clicked. Clicking the open row
   * set the index to -1, which the panel read as "fall back to the first
   * slot" — so collapsing a preview moved the panel to slot 0 while the strip
   * highlighted nothing, and slot 0's "Approved" appeared to have followed the
   * reviewer across. Reported as approval state leaking between logos.
   *
   * Null means "nothing chosen yet", which resolves to the first slot once.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * Which preview is open, held separately and deliberately.
   *
   * Expanding and selecting are two different questions. Collapsing a preview
   * must never change what the panel is about, which is exactly what the one
   * combined index made impossible.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  /**
   * Whether the relabel box is open.
   *
   * HELD HERE, not in SubmissionPanel, because the control that opens it is the
   * pencil in the heading and the heading renders for every slot — including
   * the ones with no file, which have no SubmissionPanel at all.
   */
  const [relabeling, setRelabeling] = useState(false);
  /** The submission awaiting a delete confirmation. */
  const [deleting, setDeleting] = useState<Submission | null>(null);
  const upload = useRef<HTMLInputElement>(null);
  /**
   * The slot a staff upload is destined for, held while the picker is open.
   *
   * A REF, NOT STATE, AND THAT IS THE FIX. This was `useState`, set immediately
   * before `upload.current.click()` in the same handler. A state write is not
   * visible until React re-renders, so the change handler could read the
   * PREVIOUS value: null on the first use, which files the upload as an Extra,
   * and the previously chosen version on every use after that. QA hit the first
   * case twice, on 30 August 2026 and again on retest.
   *
   * A ref is written synchronously, so the value the picker was opened for is
   * the value the upload uses. Nothing here is rendered from it, which is the
   * other half of why state was the wrong tool.
   *
   * The server was never at fault. The reported cause was that the client
   * appends the file before the variationId field, so multer might not have
   * populated req.body yet; that was tested directly against multer with both
   * orderings and req.body carries the field either way.
   */
  const uploadFor = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(base);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as {
        review: {
          slots: Slot[];
          progress: Progress;
          chasing?: Chasing;
          reviewSentAt: string | null;
          emailConfigured: boolean;
        };
      };
      setSlots(body.review.slots);
      setProgress(body.review.progress);
      setChasing(body.review.chasing ?? null);
      setSentAt(body.review.reviewSentAt);
      setEmailConfigured(body.review.emailConfigured);
    } catch {
      setError('Could not load the review.');
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(submissionId: string, changes: Record<string, unknown>) {
    setWorking(true);
    setError(null);
    try {
      const res = await fetch(`${base}/${submissionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'That did not save.');
        return;
      }
      await load();
    } finally {
      setWorking(false);
    }
  }

  async function remove(submissionId: string) {
    setWorking(true);
    setError(null);
    try {
      const res = await fetch(`${base}/${submissionId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'That did not delete.');
        return;
      }
      setDeleting(null);
      await load();
    } finally {
      setWorking(false);
    }
  }

  /**
   * Uploads on the sponsor's behalf.
   *
   * The common case this serves: the sponsor emailed the artwork to whoever
   * they already know and never opened the portal.
   */
  async function uploadForSponsor(file: File, variationId: string | null) {
    setWorking(true);
    setError(null);

    const form = new FormData();
    form.append('file', file);
    if (variationId) form.append('variationId', variationId);

    try {
      const res = await fetch(`${base}/upload`, { method: 'POST', body: form });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'That did not upload.');
        return;
      }
      await load();
    } finally {
      setWorking(false);
      if (upload.current) upload.current.value = '';
    }
  }

  async function send() {
    setWorking(true);
    setError(null);
    try {
      const res = await fetch(`${base}/send`, { method: 'POST' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? 'That did not send.');
        return;
      }
      await load();
    } finally {
      setWorking(false);
    }
  }

  if (error && !slots) return <p className="auth-error">{error}</p>;
  if (!slots || !progress) return <div className="app-loading" aria-busy="true" />;

  if (progress.requested === 0 && slots.length === 0) {
    return (
      <div className="empty">
        <p className="empty-title"><T k="review.empty.title" d="Nothing to review" /></p>
        <p className="empty-copy">
          This event has not asked for any versions yet. Set them on the event's Setup tab and the
          sponsor will see them on their upload page.
        </p>
      </div>
    );
  }

  /* Resolved by id every render. A slot that has gone away — deleted, or
     relabelled into a different position — falls back to the first, which is
     the only case where the panel's subject may change without a click. */
  const slot = slots.find((s) => slotKey(s) === selectedId) ?? slots[0];
  const expandedSlot = slots.find((s) => slotKey(s) === expandedId) ?? null;
  /* slots is never empty by the time this renders — the guard above returns
     early — but the lookup above is still an optional one, so the key is read
     defensively rather than asserted. */
  const selectedKey = slot ? slotKey(slot) : '';

  /**
   * Choosing a row.
   *
   * Clicking a DIFFERENT row selects it and opens its preview. Clicking the
   * row that is already selected toggles only the preview, leaving the panel
   * where it is.
   *
   * Any change of selection also cancels a pending delete. Without that, a
   * confirmation started on one file rendered under the next file's heading
   * and deleted the first one on confirm.
   */
  const choose = (s: Slot) => {
    const id = slotKey(s);
    if (id === selectedId) {
      setExpandedId((open) => (open === id ? null : id));
      return;
    }
    /* Closed on every move, or the box would reopen under the next version
       with the previous one's selects still in it. */
    setRelabeling(false);
    setSelectedId(id);
    /* Every slot opens, including one with no file. The panel below says what
       is happening either way, and a selection that silently opened nothing
       was indistinguishable from a click that had not registered. */
    setExpandedId(id);
    setDeleting(null);
  };

  return (
    <div className="sgrid">
      <input
        ref={upload}
        type="file"
        className="portal-file"
        accept=".svg,.eps,.ai,.pdf,.png,.jpg,.jpeg,image/svg+xml,application/pdf,application/postscript,image/png,image/jpeg"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void uploadForSponsor(file, uploadFor.current);
        }}
      />

      <div>
        <p className="rv-eyebrow">
          Versions for this event{' '}
          <span className="rv-eyebrow-sub">· select one to review it</span>
        </p>

        {/* A WRAPPING ROW OF CARDS, not a vertical list. The reviewer's
            complaint was that the screen is hard to scan, and nine versions
            stacked one per row is nine screens of scrolling to see what a
            sponsor sent. Wrapped, the whole set is visible at once. */}
        <div className="rv-strip">
          {slots.map((s) => {
            const id = slotKey(s);
            const isSelected = id === selectedKey;
            const open = id === expandedId;

            return (
              <button
                key={id}
                type="button"
                className={`rv-thumb${isSelected ? ' on' : ''}${open ? ' is-open' : ''}`}
                aria-expanded={open}
                aria-pressed={isSelected}
                onClick={() => choose(s)}
              >
                <Thumb slot={s} base={base} />
                <span className="rv-thumb-name">{s.label}</span>
                <span className="rv-thumb-sub">{s.sublabel}</span>
                <span className="rv-thumb-state">{stateLabel(s)}</span>
              </button>
            );
          })}
        </div>

        {/* Below the strip rather than pinned above it. The original reason
            for inline expansion was that a fixed stage at the top of a nine
            row list becomes a header you scroll past; a compact wrapping strip
            is two rows tall, so the stage stays beside what selected it. */}
        {expandedSlot &&
          (expandedSlot.submission ? (
            <Preview submission={expandedSlot.submission} base={base} />
          ) : (
            /* A VERSION NOBODY HAS SENT NOW OPENS TOO. Selecting one used to
               leave the area below the strip showing whatever the last
               submitted version had put there, or nothing at all, so the one
               state that needs chasing was the only one with no panel. */
            <Waiting chasing={chasing} />
          ))}

        {/* The whole set in one archive. "Every version sent", not "every
            approved version" as the prototype's subtitle promised: the
            endpoint deliberately does not filter by decision, because a
            reviewer wants the files while deciding, which is before anything
            is approved. */}
        {progress.submitted > 0 && (
          <a className="rv-dlall" href={`${base}/download`}>
            <span className="rv-dlall-t">
              <DownloadIcon />
              Download All Logo Variations
            </span>
            <span className="rv-dlall-s">
              Every version sent, as vector, PNG and JPEG, in one .zip
            </span>
          </a>
        )}
      </div>

      <div>
        {slot && (
          <div className="panel">
            <div className="panel-body">
              <p className="rv-eyebrow"><T k="review.nowReviewing" d="Now Reviewing" /></p>
              {/* THE FILE IS PART OF THE HEADING, not a three-row table below
                  it. Which version this is, and which file is standing for it,
                  are one thought; File / Format / Size as separate labelled
                  rows spent a third of the panel restating what one line
                  says. */}
              <div className="rv-id">
                <div className="rv-id-text">
                  <h3>
                    {/* An Extra's label is its filename, which the line below
                        would then repeat, so the version reads as its tag
                        alone. */}
                    {slot.variation ? `${slot.label} · ${slot.sublabel}` : slot.label}
                    {slot.variation === null && <span className="rv-tag"><T k="review.tag.extra" d="Extra" /></span>}
                  </h3>
                  <p className="fdesc">{fileLine(slot)}</p>
                </div>
                {/* Relabelling is a property of the heading: it changes which
                    requested version this file answers, which is the line
                    immediately above it. It was a "Relabel" button in a row of
                    three that also held Delete. */}
                {slot.submission && (
                  <button
                    type="button"
                    className={`rv-iconbtn${relabeling ? ' on' : ''}`}
                    title="Change which version this file satisfies"
                    aria-label="Change which version this file satisfies"
                    aria-pressed={relabeling}
                    onClick={() => setRelabeling((v) => !v)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                )}
              </div>

              {deleting ? (
                <ConfirmDelete
                  submission={deleting}
                  working={working}
                  onCancel={() => setDeleting(null)}
                  onConfirm={() => void remove(deleting.id)}
                />
              ) : slot.submission ? (
                <SubmissionPanel
                  /* Keyed by the submission, so React MOUNTS A NEW ONE when
                     the reviewer moves between slots rather than reusing this
                     one with the previous slot's note still in its state. The
                     effect that used to clear it ran after paint, which showed
                     one frame of the wrong rejection note. */
                  key={slot.submission.id}
                  base={base}
                  slot={slot}
                  slots={slots}
                  working={working}
                  relabeling={relabeling}
                  onPatch={patch}
                  onDelete={() => setDeleting(slot.submission)}
                  onReplace={() => {
                    uploadFor.current = slot.variation?.id ?? null;
                    upload.current?.click();
                  }}
                />
              ) : (
                /* The panel below the strip now explains the waiting, so this
                   side carries only the thing you can DO about it. Sponsors
                   routinely email artwork straight to whoever they already
                   know and never open the portal; without this the file has to
                   be sent back to them with a request to upload their own
                   attachment. */
                <button
                  type="button"
                  className="btn-compact rv-upload-for"
                  disabled={working}
                  onClick={() => {
                    uploadFor.current = slot.variation?.id ?? null;
                    upload.current?.click();
                  }}
                >
                  {working ? 'Uploading…' : 'Upload For Sponsor'}
                </button>
              )}

              {error && <p className="auth-error">{error}</p>}

              <div className="rv-div" />

              <p className="rv-eyebrow"><T k="review.progress.title" d="Review Progress" /></p>
              {/* The count, not a bar of it. The bar restated the same
                  fraction the line below already gives exactly, and a five
                  pixel bar over four versions reads as a texture rather than a
                  proportion. */}
              <p className="rv-prog">
                {progress.decided} of {progress.submitted} reviewed
                {progress.approved > 0 && ` · ${progress.approved} approved`}
              </p>
              <p className="rv-prog-s">
                {progress.missing > 0
                  ? `${progress.missing} version${progress.missing === 1 ? '' : 's'} still needed. They go into the review email as outstanding and do not hold up sending.`
                  : 'Every version this event asked for has been sent.'}
              </p>

              {sentAt ? (
                <p className="save-note">
                  Review sent {new Date(sentAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                  . Deciding again re-opens it.
                </p>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-compact"
                    style={{ width: '100%', marginTop: 'var(--sp4)' }}
                    disabled={working || !progress.readyToSend}
                    onClick={() => void send()}
                  >
                    Send Review to Sponsor</button>
                  <p className="fdesc" style={{ marginTop: 7 }}>
                    {progress.submitted === 0
                      ? 'Nothing has been sent for review yet.'
                      : progress.readyToSend
                        ? emailConfigured
                          ? 'One email, listing every version and any notes together. It goes to the sponsor as soon as you press this.'
                          : 'One email, listing every version and any notes together. Email is not configured yet, so this records the decision without delivering it.'
                        : `Decide on every version first. ${progress.submitted - progress.decided} still open.`}
                  </p>
                </>
              )}

              {/* Last, and shut. What was checked about this file is
                  reassurance rather than a step, so it sits below the decision
                  and the send rather than between them. */}
              {slot.submission && (
                <>
                  <div className="rv-div" />
                  <Checks submission={slot.submission} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * The thumbnail on a strip row.
 *
 * Falls back through three states rather than two: real artwork, a format tag
 * for the vectors that cannot be rendered, and an outline for a version nobody
 * has sent. That last one is the most important of the three — a missing
 * version is what somebody is being chased for.
 */
function Thumb({ slot, base }: { slot: Slot; base: string }) {
  const [failed, setFailed] = useState(false);
  const submission = slot.submission;

  if (!submission) return <span className="rv-th rv-th--empty" aria-hidden="true" />;

  /* Preference order, and each step is a real case rather than defensiveness.
     The generated thumb is the answer for everything renderable. The original
     covers the three formats a browser draws unaided, which is what an asset
     shows before the backfill reaches it. The badge covers EPS, a failed
     render, and an image that would not load. */
  const src =
    renditionSrc(submission, 'thumb', base) ??
    (DIRECTLY_PREVIEWABLE.includes(submission.mimeType) ? `${base}/${submission.id}/preview` : null);

  if (!src || failed) {
    return (
      <span className="rv-th rv-th--type" aria-hidden="true">
        {formatLabel(submission.mimeType, submission.filename)}
      </span>
    );
  }

  return (
    <span className="rv-th" aria-hidden="true">
      <img src={src} alt="" onError={() => setFailed(true)} />
    </span>
  );
}

/**
 * The expanded preview under a row.
 *
 * The checkerboard and the on-dark toggle are the point of the whole feature.
 * Whether a logo's background is genuinely transparent, or a white box that
 * will show as a rectangle on a dark banner, is the single most common reason
 * artwork gets sent back — and it is invisible in a file listing.
 */
function Preview({ submission, base }: { submission: Submission; base: string }) {
  const [onDark, setOnDark] = useState(false);
  const [failed, setFailed] = useState(false);

  const src =
    renditionSrc(submission, 'preview', base) ??
    (DIRECTLY_PREVIEWABLE.includes(submission.mimeType) ? `${base}/${submission.id}/preview` : null);
  const canPreview = Boolean(src) && !failed;
  const format = formatLabel(submission.mimeType, submission.filename);

  return (
    <div className="rv-open">
      {canPreview ? (
        <div className={`rv-stage${onDark ? ' on-dark' : ''}`}>
          {/* An <img>, deliberately. Browsers render SVG here in secure static
              mode — no scripting, no external references — which is a browser
              guarantee rather than something we check. */}
          <img
            src={src!}
            alt={`Preview of ${submission.filename}`}
            onError={() => setFailed(true)}
          />
        </div>
      ) : (
        <div className="rv-stage rv-stage--none">
          <div>
            <span className="rv-stage-type">{format}</span>
            <p className="fdesc">
              {format === 'EPS'
                ? 'EPS files are not rendered here. Download it to check the artwork.'
                : 'No preview was generated for this file. Download it to check the artwork.'}
            </p>
          </div>
        </div>
      )}

      <div className="rv-openbar">
        {canPreview && (
          <div className="chips">
            <button
              type="button"
              className={`chip${onDark ? '' : ' on'}`}
              aria-pressed={!onDark}
              onClick={() => setOnDark(false)}
            >
              On White</button>
            <button
              type="button"
              className={`chip${onDark ? ' on' : ''}`}
              aria-pressed={onDark}
              onClick={() => setOnDark(true)}
            >
              On Dark</button>
          </div>
        )}
        {/* EVERY DOWNLOAD FOR THIS VERSION, beside the artwork they are copies
            of. They used to be a "This Version" row in the right panel, which
            put Download Original a few pixels above Delete File.
            The generated formats appear only when they exist: EPS is never
            rendered, a render can fail, and an asset predating the feature has
            none until the backfill reaches it. */}
        <div className="rv-exports">
          <a className="btn-compact btn-compact--quiet" href={`${base}/${submission.id}/file`}>
            <DownloadIcon />
            {isVector(submission.mimeType) ? 'Vector · original' : 'Original'}
          </a>
          {EXPORTS.filter((e) => submission.renditions.includes(e.name)).map((e) => (
            <a
              key={e.name}
              className="btn-compact btn-compact--quiet"
              href={`${base}/${submission.id}/rendition/${e.name}/download`}
            >
              {e.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * What was checked about this file, collapsed until somebody asks.
 *
 * WHY IT IS COLLAPSED. On a clean file every line says "fine", and eight rows
 * of "fine" above the Approve button is noise that trains people to scroll
 * past. The summary carries the only bit that changes — whether anything was
 * flagged — and opens to the detail when it did.
 *
 * WHY THE SECURITY LINES ARE DRAWN FROM THE FILE RATHER THAN HARDCODED. The
 * prototype printed a fixed list, including "SVG sanitized · scripts and links
 * stripped". This system does not sanitise: services/logoIngest.ts REFUSES an
 * SVG carrying a script rather than cleaning it, which is a stronger guarantee
 * and a different sentence. A checklist that describes a pipeline nobody has
 * is worse than no checklist.
 */
function Checks({ submission }: { submission: Submission }) {
  const clean = submission.scanStatus === 'clean';
  const signals = submission.signals ?? [];
  const flagged = signals.length + (clean ? 0 : 1);

  const security: { ok: boolean; text: string }[] = [
    {
      ok: clean,
      text: clean
        ? 'Scanned for malware before it reached storage'
        : `Malware scan is ${submission.scanStatus}`,
    },
    { ok: true, text: 'File type verified by its contents, not its extension' },
  ];

  /* Only claimed for the format it is true of. */
  if (submission.mimeType === 'image/svg+xml') {
    security.push({
      ok: true,
      text: 'SVG checked at upload. Scripts and external entities are refused, not stripped',
    });
  }
  /* Only claimed where a render actually happened. */
  if (submission.renditions.length > 0) {
    security.push({ ok: true, text: 'Previews rendered out of process, never in the server' });
  }

  const quality: { ok: boolean; text: string }[] =
    signals.length > 0
      ? signals.map((s) => ({ ok: false, text: s.reviewerMessage }))
      : [{ ok: true, text: 'Nothing flagged when this was uploaded' }];

  return (
    <details className="rv-checks">
      <summary>
        <span className={`ck ${flagged ? 'ck--warn' : 'ck--ok'}`} aria-hidden="true">
          {flagged ? '!' : '✓'}
        </span>
        <span>
          {flagged
            ? `${flagged} item${flagged === 1 ? '' : 's'} flagged`
            : 'All checks passed'}
        </span>
        <svg className="rv-caret" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>

      <p className="rv-checks-grp"><T k="review.checks.security" d="Security and scan" /></p>
      <CheckList rows={security} />
      <p className="rv-checks-grp"><T k="review.checks.quality" d="Quality" /></p>
      <CheckList rows={quality} />
    </details>
  );
}

function CheckList({ rows }: { rows: { ok: boolean; text: string }[] }) {
  return (
    <ul className="checklist">
      {rows.map((r) => (
        <li key={r.text}>
          <span className={`ck ${r.ok ? 'ck--ok' : 'ck--warn'}`} aria-hidden="true">
            {r.ok ? '✓' : '!'}
          </span>
          {r.text}
        </li>
      ))}
    </ul>
  );
}

function stateLabel(slot: Slot): string {
  if (!slot.submission) return 'Not sent';
  if (slot.submission.status === 'approved') return 'Approved';
  if (slot.submission.status === 'fix_requested') return 'Changes requested';
  return 'Needs review';
}

/** The decision controls for a slot that has a file. */
function SubmissionPanel({
  base,
  slot,
  slots,
  working,
  relabeling,
  onPatch,
  onDelete,
  onReplace,
}: {
  base: string;
  slot: Slot;
  slots: Slot[];
  working: boolean;
  relabeling: boolean;
  onPatch: (id: string, changes: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
  onReplace: () => void;
}) {
  const submission = slot.submission!;
  /* Seeded once at mount. The caller keys this component by submission id, so
     moving to another slot remounts it and this starts fresh — which is what
     the effect that used to live here was trying and failing to do a frame too
     late. */
  const [note, setNote] = useState(submission.decisionNote ?? '');

  const wasCorrected = submission.correctedByUserId !== null;

  return (
    <>
      {/* WHERE IT CAME FROM, for a file that was never sent for this event.
          Carried over from the library at invite time, which is what stops a
          returning sponsor being chased for artwork we already hold. Worth
          saying loudly at the moment of approval: this is the case where a
          quiet rebrand slips through, because nobody looked at it this year
          and the sponsor never confirmed anything. */}
      {submission.carriedOverAt && (
        <p className="rv-prov">
          Carried over from the library. Not sent for this event.
          {submission.sponsorConfirmedAt
            ? ' The sponsor confirmed they are happy to reuse it.'
            : ' The sponsor has not confirmed it yet.'}
        </p>
      )}

      {/* Who supplied this. It reads as a plain fact rather than a warning,
          because uploading for a sponsor is a normal thing to do — but it has
          to be recorded, since the sponsor may later dispute what they sent. */}
      {submission.uploadSource === 'staff' && (
        <p className="rv-prov rv-prov--neutral">
          Added by {submission.uploadedByName ?? 'staff'}, not uploaded by the sponsor.
        </p>
      )}

      {/* A correction has to stay visible. Silently relabelling a file changes
          which requested version it satisfies, and the sponsor's own claim is
          the only record of what they thought they sent. */}
      {wasCorrected && submission.sponsorOrientation && (
        <p className="rv-prov">
          Relabelled by staff. The sponsor filed this as {submission.sponsorOrientation}
          {submission.sponsorTreatment ? ` · ${submission.sponsorTreatment}` : ''}.
        </p>
      )}

      {relabeling && (
        <div className="rv-edit">
          <label className="fl" htmlFor="reassign"><T k="review.relabel.label" d="Which version does this satisfy?" /></label>
          <select
            id="reassign"
            className="fi"
            value={submission.eventVariationId ?? ''}
            disabled={working}
            onChange={(e) =>
              void onPatch(submission.id, { eventVariationId: e.target.value || null })
            }
          >
            <option value="">Extra · not a requested version</option>
            {slots
              .filter((s) => s.variation)
              .map((s) => {
                /* A version already holding a DIFFERENT file cannot take this
                    one. The server refuses it safely, but offering the option
                    and then failing costs a round trip and reads as a bug; the
                    list says so up front instead. The version this file is
                    already filed under is not "taken" by anything else, so it
                    stays selectable. */
                const takenBy = s.submission;
                const occupied = Boolean(takenBy) && takenBy!.id !== submission.id;

                return (
                  <option key={s.variation!.id} value={s.variation!.id} disabled={occupied}>
                    {s.variation!.orientation} · {s.variation!.treatment}
                    {occupied ? ' (already filled)' : ''}
                  </option>
                );
              })}
          </select>
          <p className="fdesc">
            This sets which requested version the file counts for. If it matches nothing the event
            asked for, it stays an Extra. A version already holding another file has to be cleared
            first.
          </p>
        </div>
      )}

      <div className="rv-div" />

      <div className="rv-decide">
        {/* THE LABEL CHANGES WHEN THE DECISION IS MADE, and that is the fix.
            Hovering Approve on an undecided logo used to be pixel-identical to
            an approved one — same border, same colour, same background, same
            constant "Approve" label — so moving the mouse across the strip
            read as approvals following the reviewer around. A decided button
            now says so in words, and aria-pressed says it to a screen reader,
            neither of which a hover state can imitate. */}
        <button
          type="button"
          className={`rv-btn rv-btn--ok${submission.status === 'approved' ? ' on' : ''}`}
          aria-pressed={submission.status === 'approved'}
          disabled={working}
          onClick={() => void onPatch(submission.id, { status: 'approved', decisionNote: null })}
        >
          {submission.status === 'approved' ? 'Approved' : 'Approve'}
        </button>
        <button
          type="button"
          className={`rv-btn rv-btn--no${submission.status === 'fix_requested' ? ' on' : ''}`}
          aria-pressed={submission.status === 'fix_requested'}
          disabled={working}
          onClick={() => void onPatch(submission.id, { status: 'fix_requested' })}
        >
          {submission.status === 'fix_requested' ? 'Changes Requested' : 'Request Changes'}
        </button>
        {/* Replacing sits with the two decisions because it is the third thing
            you can do about this file, and it is an icon because it is much
            rarer than either: the sponsor emailed a corrected version straight
            to somebody instead of using their link. */}
        <button
          type="button"
          className="rv-iconbtn"
          title="Upload a replacement on the sponsor's behalf"
          aria-label="Upload a replacement on the sponsor's behalf"
          disabled={working}
          onClick={onReplace}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M17 8l-5-5-5 5M12 3v12" />
          </svg>
        </button>
      </div>

      {submission.status === 'fix_requested' && (
        <div className="fg" style={{ marginTop: 'var(--sp3)' }}>
          <label className="fl" htmlFor="note"><T k="review.note.label" d="What Needs Fixing" /></label>
          <textarea
            id="note"
            className="fi ft"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. The EPS looks like an embedded photo. Could you re-export it as an outlined vector?"
          />
          <p className="fdesc">
            Nothing sends yet. This goes into the one review email, listed under this version.
          </p>
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            disabled={working || note === (submission.decisionNote ?? '')}
            onClick={() => void onPatch(submission.id, { decisionNote: note })}
          >
            Save Note</button>
        </div>
      )}

      {/* ON ITS OWN, away from everything else. Delete used to sit in a row
          beside Replace and Relabel, a few pixels from the controls somebody
          uses on every single version. It is the one action here that destroys
          something. */}
      <div className="rv-destructive">
        <button
          type="button"
          className="btn-compact btn-compact--danger"
          disabled={working}
          onClick={onDelete}
        >
          Delete File
        </button>
      </div>
    </>
  );
}

/**
 * Confirms deleting a file.
 *
 * Says plainly what happens and for how long it is undoable, because the
 * honest answer is not "this is permanent" and it is not "this is safe"
 * either — it is recoverable for thirty days and then it is not. A dialog
 * that says only "are you sure?" leaves the reader to guess which.
 */
function ConfirmDelete({
  submission,
  working,
  onCancel,
  onConfirm,
}: {
  submission: Submission;
  working: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="notice notice--danger">
      <b>Delete {submission.filename}?</b>
      <p className="fdesc" style={{ margin: '6px 0 10px' }}>
        The version goes back to being unsent, so the sponsor will be asked for it again. The file
        moves out of the library and is recoverable for 30 days, after which it is permanently
        removed from storage.
      </p>
      <div className="handoff">
        <button
          type="button"
          className="btn-compact btn-compact--quiet"
          onClick={onCancel}
          disabled={working}
        >
          Cancel
        </button>
        <button type="button" className="btn-compact btn-compact--danger" disabled={working} onClick={onConfirm}>
          {working ? 'Deleting…' : 'Delete File'}
        </button>
      </div>
    </div>
  );
}
